/** The three AutoCAD tables the chrome reads — the ENTIRE dependency it had on the old engine.
 *
 * ⚠⚠ **THIS FILE IS THE MEASUREMENT THAT MADE THE SPLIT LOOK CHEAP.** `autocad/` is 10,257 lines
 * and imports four things from outside itself: `react`, `../shells/types`,
 * `../../../api/resources`, and `../studio/geometry`. That last one — the only import into the
 * old ENGINE — turned out to be three constant tables. Not a canvas, not a model, not a
 * renderer. Three lists.
 *
 * ⇒ The chrome was already a shell over an injected drawing surface, exactly as
 * `docs/SPLIT-ASSESSMENT.md` §2 says. Lifting it is a copy and this file.
 *
 * ⚠ All three are AutoCAD's own tables and belong to the new app by rights: `LINEWEIGHTS` is the
 * pen ladder, `LTYPE_DASH` names the linetypes DXF carries, `PDMODES` is the POINT bit field.
 * `entity/document.py` holds the Python side of the first two — ⚠ **keep them in step, and add a
 * test that compares them the moment there are two of anything.** The old app grew exactly that
 * test after its linetype twins drifted once.
 */

/** AutoCAD's lineweight ladder, in millimetres. Twin of `entity.document.LINEWEIGHTS`, which
 *  stores the same values as INTS in 1/100 mm — the form DXF wants and the form that stops
 *  `int(0.35)` quietly becoming a hairline. */
export const LINEWEIGHTS = [0.05, 0.09, 0.13, 0.15, 0.18, 0.20, 0.25, 0.30, 0.35, 0.40, 0.50,
                            0.53, 0.60, 0.70, 0.80, 0.90, 1.00, 1.20, 1.40, 2.00];

/** Linetype NAME → the paper-mm dash pattern a renderer draws it with.
 *
 * ⚠ The name is what is stored and what the DXF carries; the array is one renderer's way of
 * drawing it. Store the array instead and the writer has to guess a name back out of "9,3" —
 * and HIDDEN, DASHED and DOT all become whichever one the guess picks. */
export const LTYPE_DASH: Record<string, string> = {
  Continuous: "",
  HIDDEN: "9,3",
  DASHED: "12,6",
  CENTER: "20,3,3,3",
  PHANTOM: "25,4,4,4,4,4",
  DOT: "0.5,4",
};

/** PDMODE — how a POINT draws. ⚠ Read as the BIT FIELD AutoCAD stores, not as a lookup: 0 dot,
 *  2 cross, 3 X, 4 tick, +32 circle, +64 square. A table would render PDMODE 35 as whatever its
 *  fallback is, and a drawing that came in from AutoCAD carrying 35 would draw as something the
 *  file does not say. */
export const PDMODES: { mode: number; label: string }[] = [
  { mode: 0, label: "Dot" },
  { mode: 1, label: "Nothing" },
  { mode: 2, label: "Cross" },
  { mode: 3, label: "X" },
  { mode: 4, label: "Tick" },
  { mode: 32, label: "Circle" },
  { mode: 33, label: "Circle + nothing" },
  { mode: 34, label: "Circle + cross" },
  { mode: 35, label: "Circle + X" },
  { mode: 36, label: "Circle + tick" },
  { mode: 64, label: "Square" },
  { mode: 65, label: "Square + nothing" },
  { mode: 66, label: "Square + cross" },
  { mode: 67, label: "Square + X" },
  { mode: 68, label: "Square + tick" },
  { mode: 96, label: "Circle + square" },
  { mode: 97, label: "Circle + square + nothing" },
  { mode: 98, label: "Circle + square + cross" },
  { mode: 99, label: "Circle + square + X" },
  { mode: 100, label: "Circle + square + tick" },
];
